import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import dropdownIcon from "@images/icons/procedure-dropdown.svg";

const Breakpoints = {
  Mobile: "768px",
  Tablet: "1024px",
};

const MediaQueries = {
  Mobile: `@media (max-width: ${Breakpoints.Mobile})`,
  Tablet: `@media (max-width: ${Breakpoints.Tablet})`,
};

// Styled components for the custom select with the new styling
const SelectWrapper = styled.div`
  position: relative;
  width: 100% !important;
  background-color: #FFFFFF4D;
  
  &.form-fields-gradient {
    position: relative;
    border-radius: 48px;
    isolation: isolate;
    
    &::before {
      content: "";
      position: absolute;
      inset: 1px;
      border-radius: 48px;
      padding: 1px;
      background: linear-gradient(150deg, rgba(255, 255, 255, 0.70) 6%, rgba(92, 143, 137, 0.60) 8%, rgba(92, 143, 137, 0.60) 91%, rgba(255, 255, 255, 0.70) 95%);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      z-index: 0;
    }
    
    box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.05) inset, 0px 20px 50px -20px rgba(0, 0, 0, 0.10);
    backdrop-filter: blur(20px);
    background: #FFFFFF4D;
  }
  
  &.form-fields-gradient-hover {
    &::before {
      background: linear-gradient(68deg, rgba(255, 255, 255, 0.70) 7%, rgba(92, 143, 137, 0.60) 10%, rgba(92, 143, 137, 0.60) 91%, rgba(255, 255, 255, 0.70) 94%);
    }
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  border-radius: 48px;
  text-align: left;
  font-size: 16px;
  font-weight: bold;
  color: #165F57;
  background-color: #FFFFFF4D;
  z-index: 20;
  position: relative;
  gap: 16px;
  
  ${MediaQueries.Tablet} {
    padding: 16px 32px;
  }
  
  padding: 23px 32px;
  
  &:hover {
    border-color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
  }
  
  ${props => props.hasError && `
    color: #E53935;
  `}
`;

// Add a new styled component for the text content
const SelectText = styled.span`
  display: flex;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  
  .additional-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const OptionsList = styled.ul`
  position: absolute;
  width: 100%;
  padding-top: 2.5rem;
  margin-top: -30px;
  z-index: -1;
  background: linear-gradient(to bottom, transparent 0%, white 15%, white 100%);
  border-radius: 0 0 32px 32px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: ${props => props.isOpen ? 'block' : 'none'};
  transition: all 100ms linear;
`;

const OptionsContainer = styled.div`
  width: 100%;
  padding: 0rem 0 1.5rem 2rem;
  position: relative;
  height: 100%;
  overflow-y: auto;
  background: white;
  backdrop-filter: blur(20px);
  z-index: -1;
  border-radius: 0 0 32px 32px;
`;

const Option = styled.li`
  width: 100%;
  padding: 0.75rem 0;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.7);
  
  &:hover, &:focus {
    font-weight: bold;
    color: #165F57;
    outline: none;
  }
  
  &[aria-selected="true"] {
    font-weight: bold;
  }
`;

const HiddenSelect = styled.select`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const ErrorMessage = styled.div`
  color: #E53935;
  font-size: 14px;
  margin-top: 4px;
`;

export const ProcedureSelect = React.forwardRef(({ 
  label, 
  name, 
  value, 
  onChange, 
  options = [], 
  required, 
  className = "",
  dropdownHeight = '',
  placeholder = 'Select...',
  additionalText = ' (Choose One)',
  error,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(opt => opt.value === value) || null
  );
  const selectRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listboxRef = useRef(null);

  // Debug
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt.value === value);
    }
  }, []);

  // Expose method to update value custom select
  React.useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const option = options.find(opt => opt.value === newValue);
      if (option) {
        setSelectedOption(option);
        
        // Also update the hidden native select for form submission
        const select = document.querySelector(`select[name="${name}"]`);
        if (select) {
          select.value = newValue;
          // Dispatch change event
          const event = new Event('change', { bubbles: true });
          select.dispatchEvent(event);
        }
      }
    }
  }), [name, options]);

  // Update selected option when value changes from parent
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt.value === value);
      if (option && (!selectedOption || selectedOption.value !== value)) {
        setSelectedOption(option);
      }
    }
  }, [value, options, selectedOption]);

  const handleSelect = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    setFocusedIndex(-1);
    
    // Update the native select
    const select = document.querySelector(`select[name="${name}"]`);
    if (select) {
      select.value = option.value;
      // Create and dispatch a native change event
      const event = new Event('change', { bubbles: true });
      select.dispatchEvent(event);
    }
    
    onChange({ 
      target: { 
        name, 
        value: option.value 
      } 
    });
    selectRef.current?.querySelector('[role="combobox"]')?.focus();
  };

  // Listen for native select changes
  useEffect(() => {
    const select = document.querySelector(`select[name="${name}"]`);
    if (select) {
      const handleChange = (e) => {
        const option = options.find(opt => opt.value === e.target.value);
        if (option && option.value !== selectedOption?.value) {
          setSelectedOption(option);
        }
      };
      select.addEventListener('change', handleChange);
      return () => select.removeEventListener('change', handleChange);
    }
  }, [name, options, selectedOption]);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const options = listboxRef.current.querySelectorAll('[role="option"]');
      if (options[focusedIndex]) {
        options[focusedIndex].scrollIntoView({ block: 'nearest' });
        options[focusedIndex].focus();
      }
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyboardNavigation = (e) => {
      if (!isOpen) return;
      
      switch(e.key) {
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(options.length - 1);
          break;
        case 'PageUp':
          e.preventDefault();
          setFocusedIndex(prev => Math.max(0, prev - 5));
          break;
        case 'PageDown':
          e.preventDefault();
          setFocusedIndex(prev => Math.min(options.length - 1, prev + 5));
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyboardNavigation);
      return () => document.removeEventListener('keydown', handleKeyboardNavigation);
    }
  }, [isOpen, options.length]);

  const handleOptionKeyDown = (e, option, index) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleSelect(option);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : options.length - 1
          );
        } else {
          setFocusedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : 0
        );
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus first option when opening
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  };
  
  // Combine provided className with the new Tailwind classes
  const containerClassName = `${className} w-full z-20 group form-fields-gradient border-gradient-button`;
  const errorClassName = error ? "error-active" : "";

  return (
    <SelectWrapper ref={selectRef} className={containerClassName}>
      
      <SelectButton
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? `label-${name}` : undefined}
        aria-controls={isOpen ? `listbox-${name}` : undefined}
        role="combobox"
        onClick={toggleDropdown}
        hasError={error}
        disabled={props.disabled}
        className={`selectTrigger ${errorClassName}`}
        {...props}
      >
        <SelectText className="text-[14px] lg:text-p text-dark_green">
          <span className="font-bold">{placeholder} </span>
          {!selectedOption && additionalText && <span className="additional-text font-normal">{additionalText}</span>}
          {selectedOption && options.some(opt => opt.value === selectedOption.value) && <span className="additional-text font-normal"> {selectedOption.label}</span>}
          {selectedOption && !options.some(opt => opt.value === selectedOption.value) && <span className="additional-text font-regular"> {additionalText}</span>}
        </SelectText>

        <img 
          src={dropdownIcon} 
          alt="dropdown" 
          className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </SelectButton>
      
      <OptionsList
        id={`listbox-${name}`}
        role="listbox"
        aria-labelledby={label ? `label-${name}` : undefined}
        isOpen={isOpen}
        dropdownHeight={dropdownHeight}
        ref={listboxRef}
        className="customSelectDropdownParent shadow-custom_select rounded-bottom_32"
      >
        <OptionsContainer className="customSelectDropdown">
          {options.map((option, index) => (
            <Option
              key={`${option.value}-${index}`}
              role="option"
              aria-selected={selectedOption?.value === option.value}
              onClick={() => handleSelect(option)}
              onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
              tabIndex={focusedIndex === index ? 0 : -1}
              className="customSelectDropdownOption"
            >
              {option.label}
            </Option>
          ))}
        </OptionsContainer>
      </OptionsList>
      
      {/* Hidden native select for form submission */}
      <HiddenSelect
        name={name}
        value={selectedOption?.value || ''}
        required={required}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </HiddenSelect>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SelectWrapper>
  );
});

export default ProcedureSelect;